import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { PoziomTreningu } from "@shared/treningTypy.js";
import { IconOstrzezenie } from "../../components/icons/index.js";
import { ListaTreningow } from "../../components/trening/ListaTreningow.js";
import { OpisRozmowcy, WyborCwiczenia, WyborPoziomu } from "../../components/trening/KartyWyboru.js";
import { Banner } from "../../components/ui/Banner.js";
import { Button } from "../../components/ui/Button.js";
import { Card } from "../../components/ui/Card.js";
import { Spinner } from "../../components/ui/Spinner.js";
import { StanBledu, StanLadowania, StanPusty } from "../../components/ui/stany.js";
import { useNowyTrening } from "../../hooks/useTrening.js";
import { useTytulStrony } from "../../hooks/useTytulStrony.js";
import { useZasob } from "../../hooks/useZasob.js";
import {
  grupujCwiczenia,
  kluczCwiczenia,
  odczytajParametryCwiczenia,
  opisBleduZKodu,
  wybierzCwiczenieStartowe,
} from "../../lib/trening.js";
import { treningApi } from "../../lib/treningApi.js";

/**
 * Ekran "Ćwiczenia" (konto `user`): wybor cwiczenia z metodyki, poziomu trudnosci i (opcjonalnie) opisu rozmowcy, start
 * rozmowy z symulowanym rozmowca (zdanie pod tytulem mowi wprost, ze gra go AI) oraz lista wlasnych treningow. Rozmowa i
 * ocena zyja pod /cwiczenia/:id.
 *
 * Z ekranu wyniku analizy uzytkownik przychodzi z parametrami zapytania (`?parametr=<slug>&cwiczenie=<numer>&zrodlo=<id
 * analizy>`): cwiczenie jest wtedy zaznaczone na starcie, a `zrodlo` jedzie z zalozeniem treningu, zeby ekran rozmowy mogl
 * wrocic do wyniku. Wybor trzymamy jako "nic nie wybrano" do czasu pierwszego klikniecia: domyslne cwiczenie wynika wtedy z
 * katalogu i adresu, wiec nie ma efektu, ktory kopiuje dane do stanu.
 *
 * Katalog i lista laduja sie raz po wejsciu (bez odpytywania): lista odswieza sie przy ponownym wejsciu na ekran.
 */

const ID_BLEDU_ZALOZENIA = "trening-blad-zalozenia";

export function CwiczeniaPage(): React.JSX.Element {
  useTytulStrony("Ćwiczenia - ConvoLens");
  const [params] = useSearchParams();
  const wejscie = useMemo(() => odczytajParametryCwiczenia(params), [params]);

  const katalog = useZasob("trening-katalog", () => treningApi.katalog());
  const lista = useZasob("trening-lista", () => treningApi.lista());
  const nowy = useNowyTrening();

  const [wybranyKlucz, setWybranyKlucz] = useState<string | null>(null);
  const [wybranyPoziom, setWybranyPoziom] = useState<PoziomTreningu | null>(null);
  const [scenariusz, setScenariusz] = useState("");

  const dane = katalog.dane;
  const cwiczenia = dane?.cwiczenia ?? [];
  const poziomy = dane?.poziomy ?? [];
  const grupy = useMemo(() => grupujCwiczenia(cwiczenia), [cwiczenia]);

  const wybrane =
    cwiczenia.find((c) => kluczCwiczenia(c) === wybranyKlucz) ?? wybierzCwiczenieStartowe(cwiczenia, wejscie);
  // Uzytkownik przyszedl z wyniku rozmowy z konkretnym cwiczeniem. Katalog ma jego WERSJE do wykonania w rozmowie (inny
  // tekst niz na karcie wyniku), a gdy tego cwiczenia w katalogu nie ma, startowe jest inne niz to z adresu: mowimy to wprost.
  const zWyniku = wejscie.parametr !== null;
  const brakWKatalogu =
    zWyniku &&
    cwiczenia.length > 0 &&
    !cwiczenia.some((c) => c.lensSlug === wejscie.parametr && (wejscie.cwiczenie === null || c.indeks === wejscie.cwiczenie));
  const poziom = poziomy.find((p) => p.poziom === wybranyPoziom)?.poziom ?? poziomy[0]?.poziom ?? null;

  function zacznij(): void {
    if (!wybrane || poziom === null) return;
    const opis = scenariusz.trim();
    void nowy.zaloz(() => ({
      lensSlug: wybrane.lensSlug,
      cwiczenieIndeks: wybrane.indeks,
      poziom,
      ...(opis ? { scenariusz: opis } : {}),
      ...(wejscie.zrodlo && !nowy.zrodloNiewazne ? { zrodloAnalizyId: wejscie.zrodlo } : {}),
    }));
  }

  let kartaWyboru: React.JSX.Element;
  if (dane === null && katalog.blad !== null) {
    kartaWyboru = (
      <Card tytul="1. Wybierz ćwiczenie">
        <StanBledu
          tytul="Nie udało się wczytać ćwiczeń"
          opis={opisBleduZKodu(katalog.blad, katalog.statusBledu).zdanie}
          akcja={
            <Button wariant="secondary" onClick={katalog.odswiez}>
              Spróbuj ponownie
            </Button>
          }
        />
      </Card>
    );
  } else if (dane === null) {
    kartaWyboru = (
      <Card tytul="1. Wybierz ćwiczenie">
        <StanLadowania tekst="Wczytuję ćwiczenia..." />
      </Card>
    );
  } else if (cwiczenia.length === 0 || poziomy.length === 0) {
    kartaWyboru = (
      <Card tytul="1. Wybierz ćwiczenie">
        <StanPusty
          tytul="Nie ma jeszcze ćwiczeń do wyboru"
          opis="Ćwiczenia pojawią się tu, gdy zostaną dodane do metodyki. Zajrzyj ponownie później."
        />
      </Card>
    );
  } else {
    kartaWyboru = (
      <>
        {/* Oba banery to wariant info (ikona i tekst, ST-3), takze ten o braku cwiczenia: lista nizej pokazuje oceny,
            a w widokach oceny bursztyn (--status-warning) znaczy "najlepsze" (decyzja autora metodyki z 29.10.2026). Baner
            o braku cwiczenia byl ostrzezeniem i zostaje pilny (podmienilismy wybor uzytkownika), drugi to zwykla informacja. */}
        {zWyniku &&
          (brakWKatalogu ? (
            <Banner
              wariant="info"
              pilny
              tekst="Ćwiczenia z wyniku rozmowy nie ma teraz w katalogu, więc zaznaczyliśmy inne. Sprawdź wybór poniżej."
            />
          ) : (
            <Banner
              wariant="info"
              tekst="To wersja ćwiczenia do wykonania w rozmowie z symulowanym rozmówcą, więc brzmi inaczej niż zadanie z wyniku rozmowy."
            />
          ))}
        <Card tytul="1. Wybierz ćwiczenie">
          <WyborCwiczenia
            grupy={grupy}
            wybrane={wybrane ? kluczCwiczenia(wybrane) : null}
            onWybierz={setWybranyKlucz}
          />
        </Card>

        <Card tytul="2. Poziom trudności">
          {poziom !== null && <WyborPoziomu poziomy={poziomy} poziom={poziom} onZmiana={setWybranyPoziom} />}
        </Card>

        <Card tytul="3. Rozmówca (opcjonalnie)">
          <OpisRozmowcy wartosc={scenariusz} onZmiana={setScenariusz} />
        </Card>

        {/* B-6: akcja, ktora uruchamia rozmowe (platna po stronie serwera) - hero. B-2: jedyny primary w widoku. */}
        <div className="hero-slot">
          <button
            type="button"
            className="btn-hero"
            // B-5: blokada drugiego klikniecia bez natywnego `disabled`, ktory zabralby fokus z przycisku.
            aria-disabled={nowy.zajety || undefined}
            aria-busy={nowy.zajety || undefined}
            aria-describedby={nowy.blad ? ID_BLEDU_ZALOZENIA : undefined}
            onClick={() => {
              if (!nowy.zajety) zacznij();
            }}
          >
            {nowy.zajety && <Spinner rozmiar="s" />}
            {nowy.zajety ? "Zakładam ćwiczenie..." : "Zacznij ćwiczenie"}
          </button>
        </div>

        {nowy.blad && (
          <p className="field-error trening-blad-zalozenia" id={ID_BLEDU_ZALOZENIA} role="alert">
            <IconOstrzezenie size={14} />
            {nowy.blad.zdanie}
          </p>
        )}
      </>
    );
  }

  return (
    <>
      <h1 className="page-title">Ćwiczenia</h1>
      {/* Swiadomy wyjatek od reguly ekranow uzytkownika bez slowa "AI": tester ma wiedziec, zanim zacznie, ze rozmowce gra
          AI, a nie czlowiek (przejrzystosc; art. 50 AI Act). Zdanie stoi poza karta wyboru, wiec widac je w kazdym stanie
          katalogu. Opis: docs/widok-uzytkownika.md, "Czego uzytkownik nigdy nie widzi". */}
      <p className="page-lead">
        Poćwicz rozmowę, w której rozmówcę gra AI. Wybierz ćwiczenie i poziom trudności, a po rozmowie dostaniesz ocenę i
        wskazówkę na następny raz.
      </p>

      {kartaWyboru}

      <ListaTreningow zasob={lista} />
    </>
  );
}
